<template>
  <ResourceDialog
    @close="resetDialog"
    @save="createAgreement"
    ref="dialog"
    :processing="processing"
    :save-button-disabled="disableSaveButton"
    save-button-text="Create agreement"
    title="Create agreement"
  >
    <template #form>
      <v-row>
        <LabeledAutocomplete
          v-if="firstSignerItems?.length"
          v-model="firstSignerField"
          :items="firstSignerItems"
          :message="firstSignerLabel"
          item-title="email"
          item-value="id"
        />

        <LabeledAutocomplete
          v-if="secondSignerItems?.length"
          v-model="secondSignerField"
          :items="secondSignerItems"
          :message="secondSignerLabel"
          item-title="email"
          item-value="id"
        />
      </v-row>
    </template>
  </ResourceDialog>
</template>

<script setup>
import LabeledAutocomplete from '@/shared/components/form/LabeledAutocomplete.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useApi from '@/shared/composables/useApi';

const props = defineProps({
  licenseId: {
    type: String,
    default: null,
  },
  groupId: {
    type: String,
    default: null,
  },
  providerId: {
    type: String,
    default: null,
  },
  schemaId: {
    type: String,
    required: true,
  },
  workflowTaskId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['created']);

defineExpose({ open, close });

const { api: Api } = useApi();

const dialog = ref(null);
const effectiveProviderId = ref(null);
const firstSignerField = ref(null);
const secondSignerField = ref(null);
const processing = ref(false);
const providerMembers = ref([]);
const groupMembers = ref([]);
const schema = ref(null);

const schemaMeta = computed(() => schema.value?.meta ?? {});

const firstSignerItems = computed(() => signerItemsForRole(schemaMeta.value.signer_role));

const firstSignerLabel = computed(() => getSignerLabel(schemaMeta.value.signer_role, '1st signer'));

const secondSignerItems = computed(() => signerItemsForRole(schemaMeta.value.second_signer_role));

const secondSignerLabel = computed(() =>
  getSignerLabel(schemaMeta.value.second_signer_role, '2nd signer'),
);

const disableSaveButton = computed(() => {
  if (!effectiveProviderId.value) return true;

  if (firstSignerItems.value?.length && !firstSignerField.value) return true;

  if (
    schemaMeta.value.second_signer_role &&
    secondSignerItems.value?.length &&
    !secondSignerField.value
  ) {
    return true;
  }

  if (schemaMeta.value.second_signer_role && !secondSignerItems.value?.length) {
    return true;
  }

  if (requiresSignerSelection(schemaMeta.value.signer_role) && !firstSignerItems.value?.length) {
    return true;
  }

  return false;
});

function close() {
  dialog.value?.close();
}

async function createAgreement() {
  if (disableSaveButton.value) return;

  processing.value = true;

  const agreementPayload = {
    schema_id: props.schemaId,
    owner_id: effectiveProviderId.value,
    owner_type: 'Provider',
    signer_member_id: firstSignerField.value ?? undefined,
    second_signer_member_id: secondSignerField.value ?? undefined,
    workflow_task_id: props.workflowTaskId,
  };

  const response = await Api.organization.agreement.create(agreementPayload);
  processing.value = false;

  if (response?.status !== 201) return;

  emit('created', response.data);
  close();
}

function getSignerLabel(role, defaultText) {
  if (!role) return defaultText;

  const recipient = role === 'Family' ? 'Parent' : role;
  return `${recipient} recipient (${defaultText})`;
}

async function resolveProviderId() {
  if (props.providerId) return props.providerId;
  if (!props.licenseId) return null;

  const response = await Api.organization.license.get(props.licenseId);
  return response?.data?.provider_id ?? null;
}

async function loadGroupMembers() {
  const resolvedGroupId = await resolveGroupId();
  if (!resolvedGroupId) {
    groupMembers.value = [];
    return;
  }

  const membersResponse = await Api.organization.member.index({ group_id: resolvedGroupId });
  groupMembers.value = membersResponse?.data ?? [];
}

async function resolveGroupId() {
  if (props.groupId) return props.groupId;
  if (!props.licenseId) return null;

  const licenseResponse = await Api.organization.license.get(props.licenseId);
  const memberId = licenseResponse?.data?.member_id;
  if (!memberId) return null;

  const memberResponse = await Api.organization.member.get(memberId);
  return memberResponse?.data?.group_id ?? null;
}

async function loadProviderMembers() {
  effectiveProviderId.value = await resolveProviderId();

  if (!effectiveProviderId.value) {
    providerMembers.value = [];
    return;
  }

  const providerResponse = await Api.publicApi.organization.provider.get(effectiveProviderId.value);
  const provider = providerResponse?.data;
  if (!provider) {
    providerMembers.value = [];
    return;
  }

  const membersResponse = await Api.organization.member.index({
    provider_id: provider.id,
    active_employment: true,
    business_id: provider.business_id,
  });
  providerMembers.value = membersResponse?.data ?? [];
}

async function loadSchema() {
  if (!props.schemaId) {
    schema.value = null;
    return;
  }

  const response = await Api.publicApi.organization.schema.get(props.schemaId);
  schema.value = response?.data ?? null;
}

async function open() {
  resetDialog();
  await Promise.all([loadSchema(), loadProviderMembers(), loadGroupMembers()]);
  await nextTick();
  dialog.value?.open({});
}

function resetDialog() {
  effectiveProviderId.value = null;
  firstSignerField.value = null;
  secondSignerField.value = null;
}

function requiresSignerSelection(role) {
  return ['Provider', 'Parent', 'Family'].includes(role);
}

function signerItemsForRole(role) {
  if (role === 'Provider') return providerMembers.value;
  if (role === 'Parent' || role === 'Family') return groupMembers.value;
  return null;
}
</script>
